import { cn } from '@/util/cn';
import { useCallback, useEffect, useState } from 'react';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';
import { localeConfig } from '@/localeConfig';

export interface ILangOption {
  value: ELocale,
  label: string,
  title: string,
  flag: string
}

export const options: Array<ILangOption> = [
  {
    value: ELocale.EN_US,
    label: 'US',
    title: 'US',
    flag: '/assets/flags/US.png',
  },
  {
    value: ELocale.EN_GB,
    label: 'UK',
    title: 'UK',
    flag: '/assets/flags/UK.png',
  },
  {
    value: ELocale.SV_SE,
    label: 'SV',
    title: 'SV',
    flag: '/assets/flags/SV.png',
  },
  {
    value: ELocale.SK_SK,
    label: 'SK',
    title: 'SK',
    flag: '/assets/flags/SK.png',
  },
  {
    value: ELocale.PL_PL,
    label: 'PL',
    title: 'PL',
    flag: '/assets/flags/PL.png',
  },
  {
    value: ELocale.DE_DE,
    label: 'DE',
    title: 'DE',
    flag: '/assets/flags/DE.png',
  },
  {
    value: ELocale.ES_ES,
    label: 'ES',
    title: 'ES',
    flag: '/assets/flags/ES.png',
  },
  {
    value: ELocale.IT_IT,
    label: 'IT',
    title: 'IT',
    flag: '/assets/flags/IT.png',
  },
  {
    value: ELocale.NN_NO,
    label: 'NO',
    title: 'NO',
    flag: '/assets/flags/NO.png',
  },
  {
    value: ELocale.DA_DK,
    label: 'DA',
    title: 'DA',
    flag: '/assets/flags/DA.png',
  },
  {
    value: ELocale.FR_FR,
    label: 'FR',
    title: 'FR',
    flag: '/assets/flags/FR.png',
  },
  {
    value: ELocale.NL_NL,
    label: 'NL',
    title: 'NL',
    flag: '/assets/flags/NL.png',
  },
  {
    value: ELocale.CS_CZ,
    label: 'CZ',
    title: 'CZ',
    flag: '/assets/flags/CS.png',
  },
];

export type SimpleLangSelectProps = {
  isOnSummerOverview?: boolean;
};

// `.lang-select` is a literal GLOBAL class on each dropdown link, used
// by the click-outside listener (`target.closest('.lang-select')`) to
// decide whether to close the dropdown. Must NOT be migrated to a
// CSS-module hash or renamed — load-bearing.
//
// `$size-base` is 0.85rem; 0.5x = 0.425rem. tracking + colors per scss.

// Compact lang select for the v3 header. Button shrinks from the
// legacy 120x29 chip to a flag+label row that hugs its content and
// sits at h-8 to line up with the search box / nav links. Dropdown
// panel widens slightly and sits below the button via top-full.
const wrapperClass = 'relative';

const buttonClass = [
  'flex h-8 items-center gap-2 px-3 rounded',
  'text-xs font-medium text-white cursor-pointer select-none',
  'hover:text-white hover:no-underline',
].join(' ');

const baseDropdownClass = [
  'absolute right-0 top-full z-[1000] min-w-[140px]',
  'mt-1 py-1.5 px-0',
  'text-left list-none',
  'rounded-md border border-border bg-white shadow-lg ring-1 ring-black/5',
  'hidden',
  '[&_a]:flex [&_a]:items-center [&_a]:gap-2',
  '[&_a]:h-9 [&_a]:px-3',
  '[&_a]:text-sm [&_a]:font-medium [&_a]:text-foreground',
  '[&_a]:no-underline',
].join(' ');

const winterDropdownClass = [
  baseDropdownClass,
  '[&_a]:hover:bg-primary [&_a]:hover:text-white',
].join(' ');

const summerDropdownClass = [
  baseDropdownClass,
  '[&_a]:hover:bg-primary-summer [&_a]:hover:text-white',
].join(' ');

const visibleClass = '!block';

export const SimpleLangSelect = ({
  isOnSummerOverview,
}: SimpleLangSelectProps) => {
  const [visible, setVisible] = useState<boolean>(false);

  const dropdownClass = isOnSummerOverview ? summerDropdownClass : winterDropdownClass;

  const links = options.map((o) => (
    <a className="lang-select" key={o.flag} href={localeConfig[o.value as ELocale].websiteDomain}>
      {visible && (
        <img
          src={o.flag}
          alt={o.label}
          width={18}
          height={18}
          title={o.title}
        />
      )}
      <span>{o.title}</span>
    </a>
  ));

  const value = options.find((o) => o.value === getLocale())!;

  const hideLangSelect = useCallback(() => {
    setVisible(false);
  }, [setVisible]);

  const showLangSelect = useCallback(() => {
    setVisible(true);
  }, [setVisible, hideLangSelect]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // If clicked outside of the dropdown
      if (!target.closest('.lang-select')) {
        hideLangSelect();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [visible, hideLangSelect]);

  return (
    <div className={wrapperClass}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/no-static-element-interactions */}
      <a
        onClick={showLangSelect}
        className={buttonClass}
      >
        <img
          src={value.flag}
          alt={value.label}
          width={18}
          height={18}
          title={value.title}
        />
        <span>{value.title}</span>
      </a>
      <div className={cn(dropdownClass, visible && visibleClass)}>
        {links}
      </div>
    </div>
  );
};
